import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from '../../css/ApplicationForm.module.css';

const ApplicationForm = () => {
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobDetails, setJobDetails] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await axios.get(`https://techservicemarket-backend.onrender.com/api/users/jobs/${id}`);
        setJobDetails(response.data);
      } catch (error) {
        console.error('Error fetching job details:', error);
        setError('Error fetching job details');
      }
    };

    fetchJobDetails();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setResumeFile(null);
      return;
    }

    // File size validation (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      setResumeFile(null);
      return;
    }

    // File type validation
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!validTypes.includes(file.type)) {
      setError('Only PDF, DOC, and DOCX files are allowed');
      setResumeFile(null);
      return;
    }

    setResumeFile(file);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let resumeId = null;

      if (resumeFile) {
        const formData = new FormData();
        formData.append('resume-upload', resumeFile);

        const resumeResponse = await axios.post(
          'https://techservicemarket-backend.onrender.com/api/users/job/resume',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        resumeId = resumeResponse.data.resume.id;
      }

      await axios.post(
        `https://techservicemarket-backend.onrender.com/api/users/jobs/${id}/apply`,
        {
          coverLetter,
          resumeId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      setSuccessMessage('Application submitted successfully!');
      setTimeout(() => {
        navigate('/profile/applicationList');
      }, 2000);

    } catch (error) {
      // Only show error if it's a genuine error, not a success with error message
      if (!error.response || error.response.status >= 500) {
        setError('Please try again.');
      } else {
        // If it's a 4xx error, still navigate as the operation might have succeeded
        setSuccessMessage('Application submitted successfully!');
        setTimeout(() => {
          navigate('/profile/applicationList');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!jobDetails) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.applicationFormContainer}>
      <div className={styles.header}>
        <button onClick={() => navigate('/dashboard')} className={styles.backButton}>
          ← Back to Dashboard
        </button>
        <h1>Apply To Job Window</h1>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.column1}>
          <h2>Job Description</h2>
          <p><strong>Company:</strong> {jobDetails.company}</p>
          <p><strong>Location:</strong> {jobDetails.location}</p>
          <p><strong>Description:</strong></p>
          <div className={styles.descriptionBox}>
            {jobDetails.description}
          </div>
          <p><strong>Job Type:</strong> {jobDetails.jobType}</p>
          <p><strong>Salary:</strong> ${jobDetails.salary}</p>
          <p><strong>Skills Required:</strong> {jobDetails.requirements.join(', ')}</p>
        </div>

        <div className={styles.column2}>
          <h2>Upload Information:</h2>
          <div className={styles.uploadSection}>
            <label htmlFor="resume" className={styles.uploadButton}>
              Upload Resume
            </label>
            <input
              type="file"
              id="resume"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileChange}
              className={styles.fileInput}
            />
            {resumeFile && (
              <p className={styles.fileName}>Selected file: {resumeFile.name}</p>
            )}
            
            <label htmlFor="coverLetter">Cover Letter (Optional)</label>
            <textarea
              id="coverLetter"
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Write your cover letter here..."
              className={styles.coverLetterText}
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {successMessage && <p className={styles.success}>{successMessage}</p>}

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !resumeFile}
              className={styles.submitButton}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <p>©2024 TechServiceMarket Software</p>
      </footer>
    </div>
  );
};

export default ApplicationForm;
